import * as types from '../../../types';
import * as notify from '../../../../lang/en/notify';
import {RepositoryFactory} from '../../../../repositories/RepositoryFactory';
import {percentOf} from '../../../../utils';

const ShopifyPriceRuleRepository = RepositoryFactory.get('acadia/shopify/discounts/price-rule');

export default {
  state: {
    typeSelected: '',
    code: '',
    fixed: 0.00,
    percent: 0.00,
    priceRule: null,
  },
  mutations: {
    [types.ORDER_SET_DISCOUNT_CODE](state, code = '') {
      state.code = code;
    },
    [types.ORDER_SET_DISCOUNT_FIXED](state, fixed = 0.00) {
      state.fixed = fixed;
    },
    [types.ORDER_SET_DISCOUNT_PERCENT](state, percent = 0.00) {
      state.percent = percent;
    },
    [types.ORDER_SET_DISCOUNT_TYPE](state, selected = null) {
      state.typeSelected = selected;
    },
    [types.ORDER_SET_DISCOUNT_PRICE_RULE](state, priceRule = null) {
      state.priceRule = priceRule;
    },
  },
  actions: {
    [types.ORDER_DISCOUNT_APPLY_PRICE_RULE]({commit, dispatch}, priceRule) {
      commit(types.ORDER_SET_DISCOUNT_PRICE_RULE, priceRule);
      dispatch(types.ORDER_PRODUCTS_UPDATE_TOTALS, {}, {root: true});
      dispatch(types.SUCCESS, notify.DISCOUNT_ADDED);
    },
    [types.ORDER_DISCOUNT_APPLY_CODE]({state, dispatch}) {
      dispatch(types.ORDER_DISCOUNT_SHOPIFY_SEARCH, {
        code: state.code,
        strict: true,
      });
    },
    [types.ORDER_DISCOUNT_APPLY_FIXED]({state, dispatch}) {
      dispatch(types.ORDER_DISCOUNT_APPLY_PRICE_RULE, {
        value: -1 * Number(state.fixed),
        value_type: 'fixed_amount',
        allocation_method: 'across',
        target_selection: 'all',
        target_type: 'line_item',
      });
    },
    [types.ORDER_DISCOUNT_APPLY_PERCENT]({state, dispatch}) {
      dispatch(types.ORDER_DISCOUNT_APPLY_PRICE_RULE, {
        value: -1 * Number(state.percent),
        value_type: 'percentage',
        allocation_method: 'across',
        target_selection: 'all',
        target_type: 'line_item',
      });
    },
    [types.ORDER_DISCOUNT_INIT]({commit, dispatch}, order) {
      commit(types.ORDER_SET_DISCOUNT_CODE);
      commit(types.ORDER_SET_DISCOUNT_FIXED);
      commit(types.ORDER_SET_DISCOUNT_PERCENT);
      commit(types.ORDER_SET_DISCOUNT_TYPE);
      commit(types.ORDER_SET_DISCOUNT_PRICE_RULE);

      const {applied_discount: appliedDiscount} = order;

      if (appliedDiscount) {
        const {value_type: valueType, title, value} = appliedDiscount;

        if (title !== 'custom') {
          return dispatch(types.ORDER_DISCOUNT_SHOPIFY_SEARCH, {
            code: title,
            strict: false,
          });
        }

        if (valueType === 'percentage') {
          commit(types.ORDER_SET_DISCOUNT_TYPE, 'Percent');
          commit(types.ORDER_SET_DISCOUNT_PERCENT, Number(value));
          return dispatch(types.ORDER_DISCOUNT_APPLY_PERCENT);
        } else if (valueType === 'fixed') {
          commit(types.ORDER_SET_DISCOUNT_TYPE, 'Fixed');
          commit(types.ORDER_SET_DISCOUNT_FIXED, Number(value));
          return dispatch(types.ORDER_DISCOUNT_APPLY_FIXED);
        }
      } else if (order.discount_codes.length > 0) {
        order.discount_codes.forEach(discountCode => {
          dispatch(types.ORDER_DISCOUNT_SHOPIFY_SEARCH, {
            code: discountCode.code,
            strict: false,
          });
        });
      }
    },
    [types.ORDER_DISCOUNT_REMOVE_CODE]({commit, dispatch}) {
      commit(types.ORDER_SET_DISCOUNT_PRICE_RULE);
      dispatch(types.ORDER_PRODUCTS_UPDATE_TOTALS, {}, {root: true});
    },
    async [types.ORDER_DISCOUNT_SHOPIFY_SEARCH]({commit, dispatch, rootState}, payload) {
      const {order} = rootState;
      const {region} = order;
      const {code: discountCode, strict} = payload;

      try {
        const response = await ShopifyPriceRuleRepository.discountCodeSearch(
          region,
          discountCode,
          strict,
        );
        const {price_rule: priceRule} = response.data;

        // Need to initialize if discount code
        if (discountCode) {
          commit(types.ORDER_SET_DISCOUNT_TYPE, 'Code');
          commit(types.ORDER_SET_DISCOUNT_CODE, discountCode);
        } else if (priceRule.value_type === 'fixed_amount') {
          commit(types.ORDER_SET_DISCOUNT_TYPE, 'Fixed');
        } else if (priceRule.value_type === 'percentage') {
          commit(types.ORDER_SET_DISCOUNT_TYPE, 'Percent');
        }

        dispatch(types.ORDER_DISCOUNT_APPLY_PRICE_RULE, priceRule);
      } catch (error) {
        dispatch(types.ERROR, `Discount code '${discountCode}' is invalid.`);
        throw error;
      }
    },
  },
  getters: {
    [types.ORDER_GET_DISCOUNT_PERCENT](state, getters, rootState, rootGetters) {
      const {priceRule} = state;

      if (!priceRule) {
        return 0;
      }

      const subtotal = rootGetters[types.ORDER_GET_PRODUCTS_SUBTOTAL];
      const {value_type: type, value} = priceRule;

      switch (type) {
        case 'fixed_amount':
          // If we have the situation where the discount is greater than the
          // subtotal, then we assume 100% discount
          if (Math.abs(value) > subtotal) {
            return -1;
          }

          return Number(value) / Number(subtotal);
        case 'percentage':
          // If we have the situation where the discount is greater than the
          // subtotal, then we assume 100% discount
          if (Math.abs(value) > 100) {
            return -1;
          }

          return Number(value) / 100;
      }
    },
    [types.ORDER_GET_DISCOUNT_CODE_IS_APPLIED](state) {
      return state.code && state.typeSelected === 'Code';
    },
    [types.ORDER_GET_DISCOUNT_APPLIED](state, getters) {
      const {priceRule} = state;

      if (!priceRule || getters[types.ORDER_GET_IS_DISCOUNT_DISABLED]) {
        return null;
      }

      const {title, value, value_type} = priceRule;
      const amount = getters[types.ORDER_GET_DISCOUNT_AMOUNT];

      return {
        'description': 'OCA Discount',
        'value_type': value_type,
        'value': Math.abs(value),
        'amount': Math.abs(amount),
        'title': title ? title : 'custom',
      };
    },
    [types.ORDER_GET_DISCOUNT_AMOUNT](state, getters) {
      const {priceRule} = state;

      if (!priceRule) {
        return null;
      }


      // For line item discounts, we'll ignore them for now.
      if (getters[types.ORDER_GET_IS_DISCOUNT_DISABLED]) {
        return null;
      }

      /*
       * Using percentage to calculate fixed amount just results in all kinds of
       * rounding issues, so it's better to avoid it.
       */
      if (state.priceRule.value_type === 'fixed_amount') {
        return state.priceRule.value;
      }

      const subtotal = getters[types.ORDER_GET_PRODUCTS_SUBTOTAL];
      const discountPercent = getters[types.ORDER_GET_DISCOUNT_PERCENT];
      return percentOf(subtotal, discountPercent);
    },
    [types.ORDER_GET_IS_DISCOUNT_DISABLED]({priceRule}) {
      if (!priceRule) return false;
      // Not set up to handle line_item discounts, for now it'll be ignored on the order level
      const isLineItem = priceRule['target_type'] === 'line_item';
      /*
       * "target_selection": "entitled"
       * The target selection method of the price rule. Valid values:
       * all: The price rule applies the discount to all line items in the checkout.
       * entitled: The price rule applies the discount to selected entitlements only.
       */
      const isValid = ['each', 'entitled'].includes(priceRule['target_selection']);
      return isLineItem && isValid;
    },
  },
};
